import { type ClassValue, clsx } from 'clsx';
import sanitizeHtml from 'sanitize-html';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Waits for a specified number of milliseconds
 * @param ms The number of milliseconds to wait
 * @returns A promise that resolves after the specified time
 */
export async function wait(ms: number) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Strips markdown formatting from text
 * @param markdown The markdown text to strip
 * @returns Plain text without markdown formatting
 */
export function stripMarkdown(markdown: string | null | undefined): string {
  if (!markdown) return '';

  // First, sanitize to remove any HTML tags
  const sanitized = sanitizeHtml(markdown, { allowedTags: [] });

  // Then remove common markdown patterns
  return (
    sanitized
      // Fenced code blocks (keep inner text, remove fences)
      .replace(/```(?:[\w-]+)?\n?([\s\S]*?)```/g, '$1')
      // Headers
      .replace(/#{1,6}\s+/g, '')
      // Images
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
      // Links
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Bold, italic and strikethrough
      .replace(/~~(.*?)~~/g, '$1')
      .replace(/[*_]{1,3}(.*?)[*_]{1,3}/g, '$1')
      // Inline code
      .replace(/`([^`]+)`/g, '$1')
      // Blockquotes
      .replace(/^\s*>\s+/gm, '')
      // Lists
      .replace(/^\s*[-*+]\s+\[(?: |x|X)\]\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // Horizontal rules
      .replace(/^\s*[-*_]{3,}\s*$/gm, '')
      // Extra whitespace
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s+/g, ' ')
      .trim()
  );
}
